5/**
 * ============================================================
 * KEJJA OS — src/pages/finance/FinanceLayout.tsx  (v1.1)
 * Sub-shell for the Finance module with Bills/Funds/Credit tabs.
 * ============================================================
 */

import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import {
  Wallet, ListChecks, Target, Smartphone, Tag, TrendingUp,
  Calendar, PiggyBank, AlertTriangle,
} from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface FinanceContext {
  homeId: string;
  currencyCode: string;
  homeName: string;
}

export function useFinanceContext(): FinanceContext {
  return useOutletContext<FinanceContext>();
}

const TABS = [
  { to: '/finance',              label: 'Overview',     icon: Wallet,         end: true },
  { to: '/finance/transactions', label: 'Transactions', icon: ListChecks },
  { to: '/finance/income',       label: 'Income',       icon: TrendingUp },
  { to: '/finance/mpesa',        label: 'M-Pesa',       icon: Smartphone },
  { to: '/finance/bills',        label: 'Bills',        icon: Calendar },
  { to: '/finance/funds',        label: 'Goals',        icon: PiggyBank },
  { to: '/finance/credit',       label: 'Credit',       icon: AlertTriangle },
  { to: '/finance/budgets',      label: 'Budgets',      icon: Target },
  { to: '/finance/categories',   label: 'Categories',   icon: Tag },
];

export default function FinanceLayout() {
  const { data, isLoading } = useMyHomes();
  const first = data?.homes?.[0];
  const homeId = first?.home.id;
  const currencyCode = first?.home.currencyCode ?? 'KES';
  const homeName = first?.home.name ?? '';

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!homeId) {
    return (
      <div className="text-sm text-muted-foreground">
        No home found. Create a home first.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ============ HEADER ============ */}
      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 flex items-center justify-center flex-shrink-0">
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
              Finance
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              <span className="italic">Money flows, your way.</span>
              {homeName && <span className="ml-2">· {homeName} · {currencyCode}</span>}
            </p>
          </div>
        </div>
      </header>

      {/* ============ SUB-NAV ============ */}

{/* ============ SUB-NAV ============ */}
      <nav className="relative border-b border-border">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar -mb-px">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors relative border-b-2',
                isActive
                  ? 'text-orange-700 dark:text-orange-300 border-orange-500'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </NavLink>
          ))}
        </div>
      </nav>


      {/* ============ OUTLET ============ */}
      <Outlet context={{ homeId, currencyCode, homeName } satisfies FinanceContext} />
    </div>
  );
}
